import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { homeGroups as groups, findCategoryById, parseQuery } from '@barter/shared';
import type { AdCondition } from '@barter/shared';
import { useAds } from '../../hooks/useAds';
import { useSnapshotProgress } from '../../hooks/useSnapshotProgress';
import { useTelegramContext } from '../../context/TelegramContext';
import { AdCard } from '../../components/AdCard/AdCard';
import { SearchBar } from '../../components/SearchBar/SearchBar';
import { CitySelect } from '../../components/CitySelect/CitySelect';
import { QueryChips } from '../../components/QueryChips/QueryChips';
import { Icon } from '../../components/Icon/Icon';
import { AdSkeletonList } from '../../components/Skeleton/Skeleton';
import {
  addSearchHistoryEntry,
  getSearchHistoryEntries,
  clearSearchHistoryEntries,
  getDistinctMakes,
  getDistinctCities,
  type CityOption,
  type AdFocus,
} from '../../db/database';
import type { AdSort } from '../../db/database';
import { EmptyState } from '../../components/EmptyState/EmptyState';

const SORTS: AdSort[] = ['smart', 'newest', 'priceAsc', 'priceDesc'];
const FRESHNESS = [0, 1, 7, 30] as const;
const FOCUS_VALUES: AdFocus[] = ['cn', 'ev'];

function parseSort(value: string | null): AdSort {
  return SORTS.includes((value ?? '') as AdSort) ? (value as AdSort) : 'smart';
}

function parseDays(value: string | null): number {
  const n = Number(value);
  return FRESHNESS.includes(n as (typeof FRESHNESS)[number]) ? n : 0;
}

function parseFocus(value: string | null): AdFocus | undefined {
  return FOCUS_VALUES.includes((value ?? '') as AdFocus) ? (value as AdFocus) : undefined;
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [makes, setMakes] = useState<string[]>([]);
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(true);
  const { t } = useTranslation();
  const snapshot = useSnapshotProgress();
  const { uiMode } = useTelegramContext();
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Все фильтры живут в URL: ссылку на отфильтрованный вид можно
  // закинуть кому-то целиком, перезагрузка не теряет состояние.
  const category = searchParams.get('category') || undefined;
  const search = searchParams.get('search') || undefined;
  const conditionParam = searchParams.get('condition');
  const condition: AdCondition | '' =
    conditionParam === 'new' || conditionParam === 'used' ? conditionParam : '';
  const make = searchParams.get('make') || '';
  const model = searchParams.get('model') || '';
  const city = searchParams.get('city') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';
  const days = parseDays(searchParams.get('days'));
  const sort = parseSort(searchParams.get('sort'));
  const focusParam = parseFocus(searchParams.get('focus'));

  // Простой режим держит каталог в фокусе ниши (китайские авто + электромобили),
  // если пользователь не выбрал что-то одно кнопкой; полный режим — весь каталог.
  const focus: AdFocus | undefined = focusParam ?? (uiMode === 'simple' ? 'any' : undefined);

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        if (value) prev.set(key, value);
        else prev.delete(key);
        return prev;
      });
    },
    [setSearchParams],
  );

  const { items, total, loading, error, hasMore, loadMore } = useAds({
    category,
    search,
    city: city || undefined,
    status: 'active',
    condition: condition || undefined,
    make: make || undefined,
    model: model.trim() || undefined,
    priceMin: priceMin ? Number(priceMin) : undefined,
    priceMax: priceMax ? Number(priceMax) : undefined,
    days: days || undefined,
    focus,
    sort,
  });

  useEffect(() => {
    getSearchHistoryEntries().then(setHistory).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => {
      getDistinctMakes()
        .then((list) => {
          if (alive) setMakes(list);
        })
        .catch(() => {});
      getDistinctCities()
        .then((list) => {
          if (alive) setCityOptions(list);
        })
        .catch(() => {});
    };
    load();
    window.addEventListener('barter:new-ads', load);
    return () => {
      alive = false;
      window.removeEventListener('barter:new-ads', load);
    };
  }, []);

  const handleSearch = (value: string) => {
    setSearchParams((prev) => {
      if (value) prev.set('search', value);
      else prev.delete('search');
      return prev;
    });
    if (value.trim()) {
      setShowHistory(false);
      addSearchHistoryEntry(value.trim()).catch(() => {});
      setHistory((prev) => {
        const filtered = prev.filter((h) => h !== value.trim());
        return [value.trim(), ...filtered].slice(0, 10);
      });
    }
  };

  const handleHistoryClick = (query: string) => {
    setShowHistory(false);
    setSearchParams((prev) => {
      prev.set('search', query);
      return prev;
    });
  };

  const handleClearHistory = () => {
    setHistory([]);
    clearSearchHistoryEntries().catch(() => {});
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const selectedCategory = category ? findCategoryById(category) : null;

  // Розумний пошук: розбір вільного запиту на компоненти для чіпсів.
  const parsedQuery = useMemo(() => (search ? parseQuery(search) : null), [search]);

  // Два режими: стандартний — лише рядок пошуку + чіпси розпізнаного;
  // розширений — плюс панель точних фільтрів. Стан живе в URL (?filters=1),
  // а перехід із головної/чужим посиланням із фільтрами розкриває панель сам.
  // У простому режимі панель прихована повністю (увімкнути можна в налаштуваннях).
  const hasFilterParams = Boolean(category || condition || make || model || city || priceMin || priceMax || days);
  const showFilters = uiMode === 'full' && (searchParams.get('filters') === '1' || hasFilterParams);

  const handleChipRemove = (nextQuery: string) => {
    setSearchParams((prev) => {
      if (nextQuery) prev.set('search', nextQuery);
      else prev.delete('search');
      return prev;
    });
  };

  // Чіпси активних фільтрів (крім текстового запита — он в рядке поиска)
  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; icon?: string; clear: () => void }> = [];
    if (selectedCategory) {
      chips.push({
        key: 'category',
        label: t(selectedCategory.nameKey ?? selectedCategory.id),
        icon: selectedCategory.icon,
        clear: () => setParam('category', ''),
      });
    }
    if (focusParam)
      chips.push({
        key: 'focus',
        label: t(focusParam === 'cn' ? 'search.focusCn' : 'search.focusEv'),
        clear: () => setParam('focus', ''),
      });
    if (make) chips.push({ key: 'make', label: make, clear: () => setParam('make', '') });
    if (model.trim())
      chips.push({ key: 'model', label: model.trim(), clear: () => setParam('model', '') });
    if (city) chips.push({ key: 'city', label: city, clear: () => setParam('city', '') });
    if (condition)
      chips.push({
        key: 'condition',
        label: t(condition === 'new' ? 'search.conditionNew' : 'search.conditionUsed'),
        clear: () => setParam('condition', ''),
      });
    if (priceMin || priceMax) {
      chips.push({
        key: 'price',
        label: `${priceMin || '…'} — ${priceMax || '…'}`,
        clear: () => {
          setParam('priceMin', '');
          setParam('priceMax', '');
        },
      });
    }
    if (days)
      chips.push({
        key: 'days',
        label: t(`search.fresh${days}`),
        clear: () => setParam('days', ''),
      });
    return chips;
  }, [selectedCategory, focusParam, make, model, city, condition, priceMin, priceMax, days, t, setParam]);

  const showSkeleton = loading && items.length === 0;

  return (
    <div>
      <SearchBar
        value={search || ''}
        onChange={handleSearch}
        placeholder={t('search.placeholder')}
      />

      {parsedQuery && parsedQuery.tokens.length > 0 && (
        <QueryChips parsed={parsedQuery} onRemove={handleChipRemove} />
      )}

      {uiMode === 'full' && (
        <div className="search-toolbar">
          <button
            type="button"
            className={`search-filters-toggle${showFilters ? ' search-filters-toggle--active' : ''}`}
            onClick={() => setParam('filters', showFilters ? '' : '1')}
            aria-expanded={showFilters}
          >
            <Icon name="filter" size={15} />
            {t('search.filters')}
            {activeFilterChips.length > 0 && ` · ${activeFilterChips.length}`}
            <Icon name="chevron-down" size={15} className={showFilters ? 'search-filters-toggle__chevron--open' : undefined} />
          </button>
        </div>
      )}

      {showFilters && (
        <div className="search-advanced">
          <div className="search-filters">
            <select
              className="form-select form-select--small"
              value={sort}
              onChange={(e) => setParam('sort', e.target.value === 'smart' ? '' : e.target.value)}
              aria-label={t('search.sort')}
            >
              {SORTS.map((s) => (
                <option key={s} value={s}>
                  {t(`search.sort_${s}`)}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select--small"
              value={category || ''}
              onChange={(e) => setParam('category', e.target.value)}
            >
              <option value="">{t('search.allCategories')}</option>
              {groups.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {t(cat.nameKey ?? cat.id)}
                </option>
              ))}
            </select>
          </div>

          <div className="search-filters">
            <CitySelect
              value={city}
              onChange={(v) => setParam('city', v)}
              placeholder={t('search.city')}
              suggestions={cityOptions}
              small
            />
          </div>

          <div className="search-price-row">
            <select
              className="form-select form-select--small"
              value={make}
              onChange={(e) => setParam('make', e.target.value)}
              aria-label={t('search.make')}
            >
              <option value="">{t('search.anyMake')}</option>
              {makes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <input
              className="form-input form-input--small"
              type="text"
              value={model}
              onChange={(e) => setParam('model', e.target.value)}
              placeholder={t('search.model')}
              maxLength={60}
            />
          </div>

          <div className="search-price-row">
            <input
              className="form-input form-input--small"
              type="number"
              value={priceMin}
              onChange={(e) => setParam('priceMin', e.target.value)}
              placeholder={t('search.priceFrom')}
              min={0}
            />
            <span className="search-price-sep">—</span>
            <input
              className="form-input form-input--small"
              type="number"
              value={priceMax}
              onChange={(e) => setParam('priceMax', e.target.value)}
              placeholder={t('search.priceTo')}
              min={0}
            />
          </div>

          <div className="condition-toggle condition-toggle--filter">
            <button
              type="button"
              className={`condition-toggle__btn${!condition ? ' condition-toggle__btn--filter-active' : ''}`}
              onClick={() => setParam('condition', '')}
            >
              {t('search.conditionAny')}
            </button>
            <button
              type="button"
              className={`condition-toggle__btn${condition === 'new' ? ' condition-toggle__btn--active-new' : ''}`}
              onClick={() => setParam('condition', condition === 'new' ? '' : 'new')}
            >
              {t('search.conditionNew')}
            </button>
            <button
              type="button"
              className={`condition-toggle__btn${condition === 'used' ? ' condition-toggle__btn--active-used' : ''}`}
              onClick={() => setParam('condition', condition === 'used' ? '' : 'used')}
            >
              {t('search.conditionUsed')}
            </button>
          </div>

          <div className="seg-switch" role="group" aria-label={t('search.freshness')}>
            {FRESHNESS.map((d) => (
              <button
                key={d}
                type="button"
                className={`seg-switch__btn${days === d ? ' seg-switch__btn--active' : ''}`}
                onClick={() => setParam('days', d === 0 ? '' : String(d))}
              >
                {t(`search.fresh${d}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="search-chips">
          {activeFilterChips.map((chip) => (
            <span key={chip.key} className="search-chip">
              {chip.icon && <span className="search-chip__icon">{chip.icon}</span>}
              {chip.label}
              <button
                type="button"
                className="search-chip__remove"
                onClick={chip.clear}
                aria-label={t('search.removeChip')}
              >
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {!search && showHistory && history.length > 0 && (
        <div className="search-history">
          <div className="search-history__header">
            <span className="search-history__title">{t('search.historyTitle')}</span>
            <button className="search-history__clear" onClick={handleClearHistory}>
              {t('search.clearHistory')}
            </button>
          </div>
          <div className="search-history__list">
            {history.map((q) => (
              <button
                key={q}
                className="search-history__item"
                onClick={() => handleHistoryClick(q)}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {total > 0 && <div className="search-count">{t('search.found', { count: total })}</div>}

      {error && <div className="error-state">{error}</div>}

      <div className="offer-list">
        {items.map((ad) => (
          <AdCard key={ad.id} ad={ad} />
        ))}
      </div>

      {(showSkeleton || (snapshot.active && snapshot.firstLoad && !error && items.length === 0)) && (
        <AdSkeletonList count={3} />
      )}

      {loading && !error && items.length > 0 && <div className="loading-state">{t('common.loading')}</div>}

      {snapshot.failed && !snapshot.active && !error && items.length === 0 && (
        <div className="error-state">
          <div>{t('common.snapshotError')}</div>
          <button
            type="button"
            className="error-state__link"
            onClick={() => navigate('/settings#feedback')}
          >
            ✉️ {t('settings.feedbackNotify')}
          </button>
        </div>
      )}

      {!loading &&
        !error &&
        items.length === 0 &&
        !(snapshot.active && snapshot.firstLoad) &&
        !(snapshot.failed && !snapshot.active) &&
        !showSkeleton && (
          <EmptyState icon="🔍" text={t('search.nothingFound')} hint={t('search.tryFilters')} />
        )}

      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
}
